// ==========================================
// 1. SURVEY POPUP COMPONENT (SurveyPopup.jsx)
// ==========================================

import React, { useState, useEffect } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebase';
import './SurveyPopup.css';

const SurveyPopup = ({ isOpen, onClose, userEmail, userName, firestoreService }) => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const [experienceRating, setExperienceRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [hasUsedTools, setHasUsedTools] = useState(null);
  const [likedTools, setLikedTools] = useState('');
  const [memoryRating, setMemoryRating] = useState(0);
  const [hoveredMemoryRating, setHoveredMemoryRating] = useState(0);
  const [generalFeedback, setGeneralFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // Confetti animation
  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  const handleSkip = async () => {
    try {
      if (firestoreService?.isReady?.()) {
        const settings = await firestoreService.getUserSettings();
        const skipCount = settings?.surveySkipCount || 0;
        
        await firestoreService.updateUserData({
          'settings.surveySkipCount': skipCount + 1,
          'settings.lastSurveySkipped': new Date().toISOString()
        });
      }
      onClose();
    } catch (error) {
      console.error('Error recording skip:', error);
      onClose();
    }
  };

  const handleNext = () => {
    if (currentSlide === 1 && experienceRating > 0) {
      setCurrentSlide(2);
    } else if (currentSlide === 2 && hasUsedTools !== null) {
      setCurrentSlide(3);
    } else if (currentSlide === 3 && memoryRating > 0) {
      setCurrentSlide(4);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const settings = await firestoreService.getUserSettings();
      const skipCount = settings?.surveySkipCount || 0;
      
      const surveyRef = collection(db, 'surveys');
      await addDoc(surveyRef, {
        email: userEmail || 'anonymous',
        userName: userName || 'Anonymous',
        experienceRating,
        hasUsedTools,
        likedTools: likedTools.trim() || 'N/A',
        memoryRating,
        generalFeedback: generalFeedback.trim() || 'No feedback provided',
        skipCount,
        timestamp: new Date(),
        completedAt: new Date().toISOString()
      });

      if (firestoreService?.isReady?.()) {
        await firestoreService.updateUserData({
          'settings.surveyCompleted': true,
          'settings.surveyCompletedAt': new Date().toISOString(),
          'settings.surveySkipCount': skipCount
        });
      }

      console.log('✅ Survey submitted successfully');
      setShowConfetti(true);
      setCurrentSlide(5);
    } catch (error) {
      console.error('❌ Error submitting survey:', error);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="survey-overlay" onClick={currentSlide !== 5 ? handleSkip : null}>
      {/* CSS Confetti Animation */}
      {showConfetti && (
        <div className="confetti-container">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="confetti-piece"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                backgroundColor: ['#FFD700', '#FF69B4', '#87CEEB', '#90EE90', '#FFA500'][Math.floor(Math.random() * 5)]
              }}
            />
          ))}
        </div>
      )}
      
      <div className="survey-popup" onClick={(e) => e.stopPropagation()}>
        
        {/* SLIDE 1: Experience Rating */}
        {currentSlide === 1 && (
          <div className="survey-slide">
            <h3 className="survey-title">How's your experience?</h3>
            <p className="survey-subtitle">Help us improve Preptune</p>
            
            <div className="survey-rating-container">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  className={`survey-star ${experienceRating >= star || hoveredRating >= star ? 'active' : ''}`}
                  onClick={() => setExperienceRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                >
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </button>
              ))}
            </div>
            
            <div className="survey-rating-labels">
              <span>Not great</span>
              <span>Amazing!</span>
            </div>

            <div className="survey-buttons">
              <button onClick={handleSkip} className="survey-btn survey-btn-secondary">
                Skip
              </button>
              <button 
                onClick={handleNext} 
                className="survey-btn survey-btn-primary"
                disabled={experienceRating === 0}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* SLIDE 2: Tools Usage */}
        {currentSlide === 2 && (
          <div className="survey-slide">
            <h3 className="survey-title">Have you used any tools yet?</h3>
            <p className="survey-subtitle">Tools like Journal tool or Podcast tool</p>
            
            <div className="survey-choice-buttons">
              <button
                className={`survey-choice-btn ${hasUsedTools === true ? 'selected' : ''}`}
                onClick={() => setHasUsedTools(true)}
              >
                Yes
              </button>
              <button
                className={`survey-choice-btn ${hasUsedTools === false ? 'selected' : ''}`}
                onClick={() => setHasUsedTools(false)}
              >
                No
              </button>
            </div>

            {hasUsedTools === true && (
              <div className="survey-followup">
                <textarea
                  className="survey-textarea"
                  placeholder="Which tools do you like? What would you like to see added?"
                  value={likedTools}
                  onChange={(e) => setLikedTools(e.target.value)}
                  rows={4}
                  maxLength={300}
                />
                <div className="survey-char-count">{likedTools.length}/300</div>
              </div>
            )}

            <div className="survey-buttons">
              <button onClick={handleSkip} className="survey-btn survey-btn-secondary">
                Skip
              </button>
              <button 
                onClick={handleNext} 
                className="survey-btn survey-btn-primary"
                disabled={hasUsedTools === null}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* SLIDE 3: Memory Rating */}
        {currentSlide === 3 && (
          <div className="survey-slide">
            <h3 className="survey-title">How well does Preptune remember?</h3>
            <p className="survey-subtitle">Does it recall your past conversations?</p>
            
            <div className="survey-rating-container">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  className={`survey-star ${memoryRating >= star || hoveredMemoryRating >= star ? 'active' : ''}`}
                  onClick={() => setMemoryRating(star)}
                  onMouseEnter={() => setHoveredMemoryRating(star)}
                  onMouseLeave={() => setHoveredMemoryRating(0)}
                >
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </button>
              ))}
            </div>
            
            <div className="survey-rating-labels">
              <span>Needs work</span>
              <span>Excellent!</span>
            </div>

            <div className="survey-buttons">
              <button onClick={handleSkip} className="survey-btn survey-btn-secondary">
                Skip
              </button>
              <button 
                onClick={handleNext} 
                className="survey-btn survey-btn-primary"
                disabled={memoryRating === 0}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* SLIDE 4: General Feedback */}
        {currentSlide === 4 && (
          <div className="survey-slide">
            <h3 className="survey-title">Any other thoughts?</h3>
            <p className="survey-subtitle">Your feedback shapes what we build next</p>
            
            <textarea
              className="survey-textarea"
              placeholder="Share anything on your mind..."
              value={generalFeedback}
              onChange={(e) => setGeneralFeedback(e.target.value)}
              rows={5}
              maxLength={500}
            />
            
            <div className="survey-char-count">{generalFeedback.length}/500</div>

            <div className="survey-buttons">
              <button onClick={handleSkip} className="survey-btn survey-btn-secondary">
                Skip
              </button>
              <button 
                onClick={handleSubmit} 
                className="survey-btn survey-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        )}

        {/* SLIDE 5: Thank You */}
        {currentSlide === 5 && (
          <div className="survey-slide survey-thank-you">
            <div className="survey-thank-you-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            
            <h3 className="survey-title">Thank you!</h3>
            <p className="survey-thank-you-text">
              Your feedback helps us make Preptune better for everyone.
            </p>
            
            <div className="survey-discord-card">
              <p className="survey-discord-text">Join our Discord community to stay updated!</p>
              <a 
                href="https://discord.gg/bpkKFucBA" 
                target="_blank" 
                rel="noopener noreferrer"
                className="survey-discord-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                Join Discord
              </a>
            </div>

            <button 
              onClick={onClose}
              className="survey-btn survey-btn-primary survey-close-final"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyPopup;